import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  getExamesControllerListarQueryKey,
  getExamesControllerObterQueryKey,
  useExamesControllerSubstituirResultadosExternos,
} from '@/api/generated/exames/exames';
import type { ExameResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { RevisaoDeValores } from '@/features/exames/components/RevisaoDeValores';
import {
  linhasDoExame,
  validarValores,
  valoresParaEnvio,
  type ErrosDosValores,
  type ValorDoLaudo,
} from '@/features/exames/valoresDoLaudo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { toast } from '@/shared/ui/sonner';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  exame: ExameResponse;
}

/**
 * Corrige os valores de um exame externo DEPOIS de salvo.
 *
 * Existe porque a leitura automática erra, e o erro que mais custa não é o
 * número — é o ANALITO: o cortisol que entrou como T4 fica desenhado no
 * gráfico do T4. Por isso salvar aqui SUBSTITUI o conjunto inteiro
 * (`PUT /exames/:id/resultados-externos`): a linha que saiu da tela sai do
 * exame, e o ponto some do gráfico errado.
 */
export function EditarValoresExternosDialogo({ aberto, aoFechar, exame }: Props) {
  const queryClient = useQueryClient();
  const substituir = useExamesControllerSubstituirResultadosExternos();

  const [valores, setValores] = useState<ValorDoLaudo[]>([]);
  const [erros, setErros] = useState<ErrosDosValores>({});
  const [erroDoServidor, setErroDoServidor] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setValores(linhasDoExame(exame));
    setErros({});
    setErroDoServidor(null);
  }, [aberto, exame]);

  async function salvar() {
    const encontrados = validarValores(valores);
    setErros(encontrados);
    if (Object.keys(encontrados).length > 0) return;

    try {
      const atualizado = await substituir.mutateAsync({
        id: exame.id,
        data: valoresParaEnvio(valores),
      });

      queryClient.setQueryData(getExamesControllerObterQueryKey(exame.id), atualizado);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getExamesControllerListarQueryKey() }),
        // As séries do paciente: o valor que trocou de analito precisa sair de
        // um gráfico e entrar no outro.
        queryClient.invalidateQueries({
          predicate: (q) =>
            String(q.queryKey[0]).startsWith(`/v1/historico/pacientes/${exame.pacienteId}`),
        }),
      ]);
      toast.success('Valores atualizados.');
      aoFechar();
    } catch (erro) {
      setErroDoServidor(
        erro instanceof ApiError ? erro.message : 'Não foi possível salvar os valores.',
      );
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Valores do exame</DialogTitle>
          <DialogDescription>
            {exame.exameNome} — o que estiver nesta lista ao salvar é o que fica no exame.
          </DialogDescription>
        </DialogHeader>

        {erroDoServidor && (
          <Alert variant="destructive">
            <AlertDescription>{erroDoServidor}</AlertDescription>
          </Alert>
        )}

        <RevisaoDeValores
          valores={valores}
          aoMudar={(novos) => {
            setValores(novos);
            setErros({});
          }}
          erros={erros}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={aoFechar}
            disabled={substituir.isPending}
          >
            Cancelar
          </Button>
          <Button type="button" onClick={() => void salvar()} disabled={substituir.isPending}>
            {substituir.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar valores
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
